<template>
  <div class="bkt-player-stats">
    <div class="bkt-player-stats__body">
      <van-loading v-if="loading" vertical class="bkt-player-stats__loading">
        加载中...
      </van-loading>
      <template v-else-if="teamsOrdered.length">
        <div class="team-tabs">
          <div
            v-for="(row, idx) in teamsOrdered"
            :key="row.teamId != null ? String(row.teamId) : idx"
            class="team-tabs__item"
            :class="[
              row.isHomeTeam ? 'team-tabs__item--home' : 'team-tabs__item--away',
              { 'team-tabs__item--active': idx === activeTeamIdx },
            ]"
            role="button"
            tabindex="0"
            @click="activeTeamIdx = idx"
          >
            <div class="team-tabs__inner">
              <img
                v-if="teamBadgeUrl(row)"
                :src="teamBadgeUrl(row)"
                class="team-tabs__badge"
                alt=""
              />
              <span class="team-tabs__name">{{ teamDisplayName(row) }}</span>
            </div>
          </div>
          <div class="team-tabs__track">
            <div
              class="team-tabs__track-active"
              :style="{ transform: `translateX(${activeTeamIdx * 100}%)` }"
            ></div>
          </div>
        </div>

        <div class="stats-card">
          <div class="stats-table">
            <div class="stats-table__row stats-table__row--head">
              <span class="col-player">球员</span>
              <span class="col-stat">得分</span>
              <span class="col-stat">助攻</span>
              <span class="col-stat">篮板</span>
              <span class="col-stat">抢断</span>
              <span class="col-stat">盖帽</span>
              <span class="col-stat">犯规</span>
              <span class="col-stat">失误</span>
            </div>
            <div
              v-for="(player, pi) in currentPlayerRows"
              :key="player.playerId != null ? String(player.playerId) : 'p-' + pi"
              class="stats-table__row"
            >
              <span class="col-player">
                <span class="player-idx">{{ playerNumberText(player) }}</span>
                <span class="player-name">{{ player.userName || '—' }}</span>
              </span>
              <span class="col-stat">{{ fmt(player.score) }}</span>
              <span class="col-stat">{{ fmt(player.assist) }}</span>
              <span class="col-stat">{{ fmt(player.rebound) }}</span>
              <span class="col-stat">{{ fmt(player.steal) }}</span>
              <span class="col-stat">{{ fmt(player.block) }}</span>
              <span class="col-stat">{{ fmt(player.foul) }}</span>
              <span class="col-stat">{{ fmt(player.turnover) }}</span>
            </div>
            <div v-if="activeTeamRow" class="stats-table__row stats-table__row--total">
              <span class="col-player col-player--total">TOTAL</span>
              <span class="col-stat">{{ fmt(teamTotalScore(activeTeamRow)) }}</span>
              <span class="col-stat">{{ fmt(teamAgg(activeTeamRow, 'assist')) }}</span>
              <span class="col-stat">{{ fmt(teamAgg(activeTeamRow, 'rebound')) }}</span>
              <span class="col-stat">{{ fmt(teamAgg(activeTeamRow, 'steal')) }}</span>
              <span class="col-stat">{{ fmt(teamAgg(activeTeamRow, 'block')) }}</span>
              <span class="col-stat">{{ fmt(teamAgg(activeTeamRow, 'foul')) }}</span>
              <span class="col-stat">{{ fmt(teamTurnoverTotal(activeTeamRow)) }}</span>
            </div>
          </div>
        </div>
      </template>
      <van-empty v-else description="暂无球员数据" />
    </div>
  </div>
</template>

<script>
import { NavBar, Loading, Empty } from 'vant'
import { getBMGameDetail } from '@/api/match'
import { QNImageUtil } from '@/utils'

function num(v) {
  if (v == null || v === '') return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export default {
  name: 'BasketballPlayerStats',
  components: {
    'van-nav-bar': NavBar,
    'van-loading': Loading,
    'van-empty': Empty,
  },
  data() {
    return {
      loading: true,
      playerStats: [],
      homeTeamLabel: '',
      awayTeamLabel: '',
      homeTeamId: '',
      awayTeamId: '',
      homeTeamBadge: '',
      awayTeamBadge: '',
      matchLogo: '',
      activeTeamIdx: 0,
    }
  },
  computed: {
    teamsOrdered() {
      const list = Array.isArray(this.playerStats) ? [...this.playerStats] : []
      list.sort((a, b) => {
        const ah = !!a.isHomeTeam
        const bh = !!b.isHomeTeam
        if (ah === bh) return 0
        return ah ? -1 : 1
      })
      return list
    },
    activeTeamRow() {
      return this.teamsOrdered[this.activeTeamIdx] || null
    },
    currentPlayerRows() {
      const row = this.activeTeamRow
      if (!row || !Array.isArray(row.stats)) return []
      return [...row.stats].sort((a, b) => {
        const na = this.playerNumberSortValue(a)
        const nb = this.playerNumberSortValue(b)
        if (na == null && nb == null) return 0
        if (na == null) return 1
        if (nb == null) return -1
        if (na !== nb) return na - nb
        const nameA = String(a.userName || '').trim()
        const nameB = String(b.userName || '').trim()
        return nameA.localeCompare(nameB, 'zh-CN')
      })
    },
  },
  watch: {
    '$route.params.gameId'() {
      this.bootstrap()
    },
  },
  created() {
    this.bootstrap()
  },
  methods: {
    onBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        const id = this.$route.params.gameId
        this.$router.replace({
          name: 'NewGameDetailHome',
          params: { gameId: id },
        })
      }
    },
    bootstrap() {
      this.loading = true
      this.playerStats = []
      this.activeTeamIdx = 0
      const gameId = this.$route.params.gameId
      getBMGameDetail({ gameId })
        .then(res => {
          const data = (res && res.data) || {}
          const game = data.game || {}
          this.homeTeamLabel = String(game.homeTeamAlias || game.homeTeamName || '').trim()
          this.awayTeamLabel = String(game.awayTeamAlias || game.awayTeamName || '').trim()
          this.homeTeamId =
            game.homeTeamId != null ? String(game.homeTeamId) : ''
          this.awayTeamId =
            game.awayTeamId != null ? String(game.awayTeamId) : ''
          this.homeTeamBadge =
            game.homeTeamBadge && QNImageUtil.imageView(game.homeTeamBadge)
          this.awayTeamBadge =
            game.awayTeamBadge && QNImageUtil.imageView(game.awayTeamBadge)
          const matchIcon =
            (game.match && game.match.icon) || game.matchIcon || ''
          this.matchLogo = matchIcon ? QNImageUtil.imageView(matchIcon) : ''
          const psRaw = Array.isArray(game.playerStats)
            ? game.playerStats
            : Array.isArray(data.playerStats)
              ? data.playerStats
              : []
          this.playerStats = psRaw
        })
        .catch(() => {
          this.playerStats = []
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            if (
              this.teamsOrdered.length &&
              this.activeTeamIdx >= this.teamsOrdered.length
            )
              this.activeTeamIdx = 0
          })
        })
    },
    teamDisplayName(teamRow) {
      if (!teamRow) return '—'
      if (teamRow.isHomeTeam) return this.homeTeamLabel || '主队'
      return this.awayTeamLabel || '客队'
    },
    teamBadgeUrl(teamRow) {
      if (!teamRow) return ''
      const tid = teamRow.teamId != null ? String(teamRow.teamId) : ''
      let badge = ''
      if (tid && this.homeTeamId && tid === this.homeTeamId) {
        badge = this.homeTeamBadge
      } else if (tid && this.awayTeamId && tid === this.awayTeamId) {
        badge = this.awayTeamBadge
      } else {
        badge = teamRow.isHomeTeam ? this.homeTeamBadge : this.awayTeamBadge
      }
      return badge || this.matchLogo
    },
    playerNumberRaw(player) {
      if (!player) return ''
      const raw = player.userNumber ?? player.number ?? player.jerseyNumber
      if (raw == null || raw === '') return ''
      const s = String(raw).trim()
      if (!s || s === '.' || s === 'null' || s === 'undefined') return ''
      return s
    },
    playerNumberSortValue(player) {
      const raw = this.playerNumberRaw(player)
      if (!raw) return null
      const n = Number(raw)
      return Number.isFinite(n) ? n : null
    },
    playerNumberText(player) {
      const raw = this.playerNumberRaw(player)
      return raw || '—'
    },
    fmt(v) {
      const n = num(v)
      return n
    },
    teamAgg(teamRow, key) {
      if (!teamRow) return 0
      if (teamRow[key] != null && teamRow[key] !== '') return num(teamRow[key])
      const stats = Array.isArray(teamRow.stats) ? teamRow.stats : []
      return stats.reduce((s, p) => s + num(p[key]), 0)
    },
    teamTotalScore(teamRow) {
      return this.teamAgg(teamRow, 'score')
    },
    teamTurnoverTotal(teamRow) {
      if (!teamRow) return 0
      if (teamRow.turnover != null && teamRow.turnover !== '')
        return num(teamRow.turnover)
      const stats = Array.isArray(teamRow.stats) ? teamRow.stats : []
      return stats.reduce((s, p) => s + num(p.turnover), 0)
    },
  },
}
</script>

<style scoped lang="scss">
.bkt-player-stats {
  min-height: 100vh;
  background: #fff;
  box-sizing: border-box;

  &__loading {
    padding: 48px 0;
    text-align: center;
    color: #969799;
  }
}

.team-tabs {
  position: relative;
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  background: #fff;

  &__item {
    flex: 0 0 50%;
    width: 50%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    padding: 10px 12px;
    box-sizing: border-box;
    cursor: pointer;

    &--home .team-tabs__inner {
      justify-content: flex-start;
    }

    &--away .team-tabs__inner {
      justify-content: flex-end;
      flex-direction: row-reverse;
      > * + * {
        margin-left: 0;
        margin-right: 8px;
      }
    }

    &--active {
      .team-tabs__name {
        color: #337ecc;
        font-weight: 600;
      }
    }

    &:not(&--active) {
      .team-tabs__name {
        color: #303133;
        font-weight: 500;
      }
    }
  }

  &__inner {
    display: flex;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    > * + * {
      margin-left: 8px;
    }
  }

  &__badge {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: #ebedf0;

    &--placeholder {
      display: inline-block;
    }
  }

  &__name {
    font-size: 14px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__track {
    flex: 0 0 100%;
    width: 100%;
    height: 3px;
    background: #c7c7c7;
    position: relative;
    overflow: hidden;
  }

  &__track-active {
    position: absolute;
    left: 0;
    top: 0;
    width: 50%;
    height: 100%;
    background: #337ecc;
    transition: transform 0.25s ease;
  }
}

.stats-card {
  background: #fff;
}

.stats-table {
  width: 100%;

  &__row {
    display: grid;
    grid-template-columns: minmax(96px, 1.35fr) repeat(7, minmax(0, 1fr));
    align-items: center;
    min-height: 44px;
    padding: 0 12px;
    box-sizing: border-box;
    font-size: 12px;
    color: #303133;

    &--head {
      min-height: 36px;
      padding-top: 12px;
      padding-bottom: 8px;
      color: #909399;
      font-size: 12px;
      font-weight: 500;
    }

    &--total {
      min-height: 48px;
      margin-top: 4px;
      background: #ecf5ff;
      font-weight: 600;
      color: #303133;
    }

    &:not(&--head):not(&--total) {
      border-top: 1px solid #f5f6f7;
    }
  }

  .col-player {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    padding: 10px 0;
    line-height: 1.35;
    text-align: left;
    > * + * {
      margin-left: 6px;
    }

    &--total {
      padding-left: 20px;
      color: #000;
      font-size: 13px;
      letter-spacing: 0.5px;
    }
  }

  .col-stat {
    text-align: center;
    padding: 10px 0;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }

  .player-idx {
    flex-shrink: 0;
    min-width: 14px;
    color: #909399;
    font-size: 12px;
    font-weight: 400;
    text-align: right;
  }

  .player-name {
    flex: 1;
    min-width: 0;
    font-weight: 500;
    word-break: break-word;
  }
}
</style>
